import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useHypotheses, HypothesisStatus } from '@/hooks/useHypotheses';
import { UserMenu } from '@/components/UserMenu';
import { WorkspaceAvatar } from '@/components/WorkspaceAvatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import quentariLogo from '@/assets/quentari-logo.png';
import { 
  Loader2, 
  ArrowLeft, 
  Lightbulb, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  PlayCircle,
  TrendingUp,
  Target,
  BarChart3
} from 'lucide-react';

const statusConfig: Record<HypothesisStatus, { label: string; color: string; icon: React.ElementType }> = {
  pending: { label: 'Offen', color: 'hsl(var(--muted-foreground))', icon: Clock },
  in_progress: { label: 'In Bearbeitung', color: 'hsl(var(--primary))', icon: PlayCircle },
  validated: { label: 'Validiert', color: 'hsl(var(--success))', icon: CheckCircle2 },
  invalidated: { label: 'Widerlegt', color: 'hsl(var(--destructive))', icon: XCircle },
};

const chartConfig: ChartConfig = {
  pending: { label: 'Offen', color: 'hsl(var(--muted-foreground))' },
  in_progress: { label: 'In Bearbeitung', color: 'hsl(var(--primary))' },
  validated: { label: 'Validiert', color: 'hsl(var(--success))' },
  invalidated: { label: 'Widerlegt', color: 'hsl(var(--destructive))' },
};

const Hypotheses = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { workspace, loading: workspaceLoading } = useWorkspace();
  const { hypotheses, isLoading, updateHypothesis, deleteHypothesis } = useHypotheses(workspace?.id);
  const [activeTab, setActiveTab] = useState<'overview' | 'list'>('overview');
  const [statusFilter, setStatusFilter] = useState<HypothesisStatus | 'all'>('all');

  if (authLoading || workspaceLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Wird geladen...</p>
        </div>
      </div>
    );
  }

  // Calculate stats
  const statusCounts = hypotheses.reduce((acc, h) => {
    acc[h.status] = (acc[h.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const totalCount = hypotheses.length;
  const completedCount = (statusCounts.validated || 0) + (statusCounts.invalidated || 0);
  const completionRate = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const validationRate = completedCount > 0 
    ? Math.round(((statusCounts.validated || 0) / completedCount) * 100) 
    : 0;

  // Pie chart data
  const pieData = Object.entries(statusCounts).map(([status, count]) => ({
    name: statusConfig[status as HypothesisStatus]?.label || status,
    value: count,
    fill: statusConfig[status as HypothesisStatus]?.color || 'hsl(var(--muted))',
  }));

  // Sessions with most hypotheses
  const sessionStats = hypotheses.reduce((acc, h) => {
    const sessionId = h.session_id;
    if (!acc[sessionId]) {
      acc[sessionId] = {
        sessionId,
        sessionName: h.session?.summary || h.session?.decision_type || 'Unbenannte Session',
        total: 0,
        validated: 0,
        invalidated: 0,
      };
    }
    acc[sessionId].total++;
    if (h.status === 'validated') acc[sessionId].validated++;
    if (h.status === 'invalidated') acc[sessionId].invalidated++;
    return acc;
  }, {} as Record<string, { sessionId: string; sessionName: string; total: number; validated: number; invalidated: number }>);

  const barData = Object.values(sessionStats)
    .sort((a, b) => b.total - a.total)
    .slice(0, 5)
    .map(s => ({
      name: s.sessionName.length > 25 ? s.sessionName.slice(0, 25) + '...' : s.sessionName,
      validated: s.validated,
      invalidated: s.invalidated,
      pending: s.total - s.validated - s.invalidated,
    }));

  // Filtered hypotheses for list view
  const filteredHypotheses = statusFilter === 'all' 
    ? hypotheses 
    : hypotheses.filter(h => h.status === statusFilter);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-[0.02]"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 60% 40% at 10% 20%, hsl(var(--primary)) 0%, transparent 50%),
              radial-gradient(ellipse 50% 50% at 90% 80%, hsl(var(--primary)) 0%, transparent 50%)
            `,
          }}
        />
      </div>

      <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="container flex h-16 items-center justify-between max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3">
            <img
              src={quentariLogo}
              alt="Quentari"
              className="h-8 w-auto cursor-pointer transition-transform duration-300 hover:scale-105"
              onClick={() => navigate('/dashboard')}
            />
            <div className="h-6 w-px bg-border" />
            <WorkspaceAvatar size="sm" showName />
          </div>
          <UserMenu />
        </div>
      </header>

      <main className="container max-w-7xl mx-auto px-4 md:px-6 py-8 relative z-10">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="mb-4 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zum Dashboard
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Lightbulb className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Hypothesen-Übersicht</h1>
          </div>
          <p className="text-muted-foreground">
            Alle Hypothesen aus deinen Entscheidungsprozessen auf einen Blick
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Target className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{totalCount}</p>
                  <p className="text-xs text-muted-foreground">Gesamt</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-success/10">
                  <CheckCircle2 className="h-5 w-5 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{statusCounts.validated || 0}</p>
                  <p className="text-xs text-muted-foreground">Validiert</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{completionRate}%</p>
                  <p className="text-xs text-muted-foreground">Abgeschlossen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-success/10">
                  <BarChart3 className="h-5 w-5 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{validationRate}%</p>
                  <p className="text-xs text-muted-foreground">Validierungsrate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'overview' | 'list')}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Übersicht</TabsTrigger>
            <TabsTrigger value="list">Alle Hypothesen</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Status Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Status-Verteilung</CardTitle>
                  <CardDescription>Verteilung aller Hypothesen nach Status</CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <Skeleton className="h-[200px] w-full" />
                  ) : pieData.length === 0 ? (
                    <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                      Noch keine Hypothesen vorhanden
                    </div>
                  ) : (
                    <ChartContainer config={chartConfig} className="h-[200px]">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <ChartTooltip content={<ChartTooltipContent />} />
                      </PieChart>
                    </ChartContainer>
                  )}
                  <div className="flex flex-wrap gap-3 mt-4 justify-center">
                    {Object.entries(statusConfig).map(([status, config]) => (
                      <div key={status} className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: config.color }}
                        />
                        <span className="text-xs text-muted-foreground">
                          {config.label} ({statusCounts[status] || 0})
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Sessions Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Top Sessions</CardTitle>
                  <CardDescription>Sessions mit den meisten Hypothesen</CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <Skeleton className="h-[200px] w-full" />
                  ) : barData.length === 0 ? (
                    <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                      Noch keine Hypothesen vorhanden
                    </div>
                  ) : (
                    <ChartContainer config={chartConfig} className="h-[200px]">
                      <BarChart data={barData} layout="vertical">
                        <XAxis type="number" hide />
                        <YAxis 
                          type="category" 
                          dataKey="name" 
                          width={100}
                          tick={{ fontSize: 11 }}
                        />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Bar dataKey="validated" stackId="a" fill="hsl(var(--success))" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="invalidated" stackId="a" fill="hsl(var(--destructive))" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="pending" stackId="a" fill="hsl(var(--muted-foreground))" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ChartContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Progress Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Gesamtfortschritt</CardTitle>
                <CardDescription>Fortschritt bei der Validierung aller Hypothesen</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Abgeschlossen</span>
                    <span className="font-medium">{completedCount} von {totalCount}</span>
                  </div>
                  <Progress value={completionRate} className="h-2" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
                  {Object.entries(statusConfig).map(([status, config]) => {
                    const Icon = config.icon;
                    const count = statusCounts[status] || 0;
                    const percentage = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;
                    return (
                      <div key={status} className="text-center">
                        <Icon className="h-5 w-5 mx-auto mb-1" style={{ color: config.color }} />
                        <p className="text-lg font-semibold">{count}</p>
                        <p className="text-xs text-muted-foreground">{config.label} ({percentage}%)</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="list" className="space-y-4">
            {/* Filter Tabs */}
            <div className="flex flex-wrap gap-2">
              <Badge
                variant={statusFilter === 'all' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setStatusFilter('all')}
              >
                Alle ({totalCount})
              </Badge>
              {Object.entries(statusConfig).map(([status, config]) => (
                <Badge
                  key={status}
                  variant={statusFilter === status ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setStatusFilter(status as HypothesisStatus)}
                >
                  {config.label} ({statusCounts[status] || 0})
                </Badge>
              ))}
            </div>

            {/* Hypothesis List */}
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            ) : filteredHypotheses.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Lightbulb className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {statusFilter === 'all' 
                      ? 'Noch keine Hypothesen vorhanden' 
                      : `Keine Hypothesen mit Status "${statusConfig[statusFilter].label}"`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredHypotheses.map((hypothesis) => {
                  const config = statusConfig[hypothesis.status];
                  const Icon = config.icon;
                  return (
                    <Card key={hypothesis.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: config.color }} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground">{hypothesis.hypothesis}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">
                                {hypothesis.session?.summary || hypothesis.session?.decision_type || 'Session'}
                              </Badge>
                              <Badge 
                                variant="secondary" 
                                className="text-xs"
                                style={{ 
                                  backgroundColor: `${config.color}20`,
                                  color: config.color 
                                }}
                              >
                                {config.label}
                              </Badge>
                              {hypothesis.evidence && (
                                <span className="text-xs text-muted-foreground">
                                  Hat Evidenz
                                </span>
                              )}
                            </div>
                            {hypothesis.validation_notes && (
                              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                                {hypothesis.validation_notes}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/app/session/${hypothesis.session_id}/report`)}
                          >
                            Öffnen
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Hypotheses;
